import { syncCanvasPixels, watchCanvasResize } from "../webgpu-canvas";
import { timelineFrameDetail } from "@/lib/timeline/scene/TimelineFrame/frameDetail";
import { timelineVerticesFor } from "./render-frame";
import { resolveTimelineGpuScene, type TimelineGpuRuntime } from "./runtime-options";

const VERTEX_SHADER = `#version 300 es
uniform vec2 resolution;
in vec2 position;
in vec4 color;
out vec4 vColor;
void main() {
  vec2 clip = (position / resolution) * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  vColor = color;
}`;

const FRAGMENT_SHADER = `#version 300 es
precision mediump float;
in vec4 vColor;
out vec4 fragColor;
void main() {
  fragColor = vColor;
}`;

interface Drawing {
  resolution: WebGLUniformLocation | null;
}

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (shader === null) {
    throw new Error("Could not create a shader");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  return shader;
}

/** Everything the context forgets when the phone takes it away. */
function prepare(gl: WebGL2RenderingContext): Drawing {
  const program = gl.createProgram();
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER));
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
  gl.linkProgram(program);
  gl.useProgram(program);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  const position = gl.getAttribLocation(program, "position");
  const color = gl.getAttribLocation(program, "color");
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 24, 0);
  gl.enableVertexAttribArray(color);
  gl.vertexAttribPointer(color, 4, gl.FLOAT, false, 24, 8);
  gl.clearColor(0.03, 0.04, 0.07, 1);
  return { resolution: gl.getUniformLocation(program, "resolution") };
}

/** Draws the same scene as WebGPU, for browsers without it. Null when WebGL2 is missing too. */
export function startWebGl2Timeline(
  canvas: HTMLCanvasElement,
  getRuntime: () => TimelineGpuRuntime,
): (() => void) | null {
  const gl = canvas.getContext("webgl2", { alpha: false, antialias: false });
  if (gl === null) {
    return null;
  }
  let drawing = prepare(gl);
  let bufferBytes = 0;
  syncCanvasPixels(canvas);

  const onFrame = (event: Event): void => {
    syncCanvasPixels(canvas);
    if (canvas.clientWidth < 1 || canvas.clientHeight < 1 || gl.isContextLost()) {
      return;
    }
    const options = getRuntime();
    const vertices = timelineVerticesFor(
      canvas,
      resolveTimelineGpuScene(options),
      timelineFrameDetail(event)?.playback ?? options.getPlayback(),
      options.getViewportWidth(),
      options.resolveViewTransform?.() ?? options.getViewTransform(),
    );
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(drawing.resolution, canvas.width, canvas.height);
    gl.clear(gl.COLOR_BUFFER_BIT);
    // Reuse one buffer. A new one every frame can run a phone out of memory.
    if (vertices.byteLength > bufferBytes) {
      bufferBytes = vertices.byteLength * 2;
      gl.bufferData(gl.ARRAY_BUFFER, bufferBytes, gl.DYNAMIC_DRAW);
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, vertices);
    gl.drawArrays(gl.TRIANGLES, 0, vertices.length / 6);
  };
  const onLost = (event: Event): void => {
    event.preventDefault();
  };
  const onRestored = (): void => {
    drawing = prepare(gl);
    bufferBytes = 0;
  };
  const unwatch = watchCanvasResize(canvas, () => {
    syncCanvasPixels(canvas);
  });
  canvas.addEventListener("timeline-frame", onFrame);
  canvas.addEventListener("webglcontextlost", onLost);
  canvas.addEventListener("webglcontextrestored", onRestored);
  onFrame(new CustomEvent("timeline-frame"));

  return () => {
    unwatch();
    canvas.removeEventListener("timeline-frame", onFrame);
    canvas.removeEventListener("webglcontextlost", onLost);
    canvas.removeEventListener("webglcontextrestored", onRestored);
  };
}
